import React from "react";
import CommonGrid from "./CommonGrid";
import { ColumnType } from "../../../../components/Grid/constants";
import { FilterOperation } from "../../../../components/Grid/dataProvider";
import { InitiativeState, InitiativeStateRaw } from "../../enum";
import { displayHowManyDaysLeft, isOfficialAdmin } from "../../CroUtils";
import { map, join } from "lodash";
import AssigneeDropdown from "../AssigneeDropdown";

const InitiativeGrid = function(props) {
  var onAssign = props.onAssign;
  var assignees = props.assignees;
  var auth = props.auth;
  var admin = auth && isOfficialAdmin(auth);

  var rest = Object.assign({}, props);
  delete rest.auth;
  delete rest.onAssign;

  var columns = [
    {
      field: "firstName",
      displayField: "official",
      label: "Jméno",
      type: ColumnType.TEXT,
      mapper: (field) => (field ? field.firstName : ""),
      width: "13%",
    },
    {
      field: "lastName",
      displayField: "official",
      label: "Příjmení",
      type: ColumnType.TEXT,
      mapper: (field) => (field ? field.lastName : ""),
      width: "13%",
    },
    {
      field: "concatenatedWorkingPositions",
      displayField: "official",
      label: "Funkce",
      type: ColumnType.TEXT,
      mapper: (obj) =>
          obj
              ? join(
                  map(
                      obj.workingPositions,
                      ({ workingPosition: { name } }) => name,
                  ),
                  ", ",
              )
              : "",
      width: "13%",
    },
    {
      field: "concatenatedWorkingPositionOrganizations",
      displayField: "official",
      label: "Organizace",
      type: ColumnType.TEXT,
      mapper: (obj) =>
          obj
              ? join(
                  map(obj.workingPositions, ({ organization }) => organization),
                  ", ",
              )
              : "",
      width: "13%",
    },
    {
      field: "anonymous",
      displayField: "personFullName",
      filterable: true,
      filterBy: { type: ColumnType.YESNO, field: "personFullName", values: {
          "true": { operation: FilterOperation.IS_NULL },
          "false": { operation: FilterOperation.NOT_NULL }
        }},
      label: "Anonymní",
      type: ColumnType.JSX,
      jsx: row => (!row.person || (!row.person.firstName && !row.person.lastName)) && (!row.legalPerson || !row.legalPerson.name) ? "ANO" : "NE",
      width: "6%",
    },
    {
      field: "personFullName",
      displayField: "person",
      label: "Jméno a příjmení podatele",
      filterable: true,
      filterBy: { type: ColumnType.TEXT, field: "personFullName" },
      type: ColumnType.JSX,
      jsx: row => row.person && (row.person.firstName || row.person.lastName) ? ((row.person.firstName || "") + " " + (row.person.lastName || "")).trim() : (row.legalPerson ? row.legalPerson.name : "").trim(),
      width: "10%",
    },
    {
      field: "state.name",
      displayField: "state",
      label: "Stav",
      enum: InitiativeState,
      type: ColumnType.ENUM,
      mapper: (field) =>
          typeof field === "object" && field ? field.name : field || "",
      exportValue: (row, column) => {
        const fieldValue = column.displayField ? row[column.displayField] : null;
        if (fieldValue && column.enum) {
          const enumItem = column.enum.find((item) => item.id === fieldValue);
          return enumItem ? enumItem.label : "";
        }
        return "";
      },
      width: "8%",
    },
    {
      field: "sendToOrganization",
      displayField: "organization",
      label: "Postoupeno na",
      type: ColumnType.TEXT,
      exportValue: (row, column) => row.organization,
      width: "10%",
    },
    {
      field: "forwardedDate",
      displayField: "forwardedDate",
      label: "Postoupeno dne",
      type: ColumnType.DATE,
      width: "10%",
    },
    {
      sortable: true,
      filterable: true,
      filterItems: (props.forwardingReasons || []).map((r) => ({ id: r.id, label: r.name })),
      filterBy: {
        type: ColumnType.ENUM,
        field: "concatenatedForwardingReasons",
        values: (props.forwardingReasons || []).reduce((acc, r) => {
          acc[r.id] = { operation: FilterOperation.CONTAINS, value: r.name };
          return acc;
        }, {}),
      },
      field: "concatenatedForwardingReasons",
      label: "Důvod postoupení",
      type: ColumnType.JSX,
      jsx: (row) => join(map(row.forwardingReasons || [], "name"), ", "),
      width: "10%",
    },
    {
      sortable: false,
      field: "period",
      label: "Lhůta",
      type: ColumnType.JSX,
      jsx: (row) =>
          displayHowManyDaysLeft(
              row.created,
              row.state === InitiativeStateRaw.NEW ||
              row.state === InitiativeStateRaw.ACCEPT,
          ),
      width: "7%",
    },
    {
      field: "created",
      label: "Vytvořen",
      type: ColumnType.DATE,
      width: "7%",
    },
  ];

  if (admin) {
    columns.push({
      field: "assignedTo.name",
      displayField: "assignedTo",
      label: "Vyřizuje",
      type: ColumnType.JSX,
      sortable: false,
      filterable: false,
      width: "20%",
      jsx: function(row) {
        var currentId = row.assignedTo ? row.assignedTo.id : '';
        return (
            <AssigneeDropdown
                value={currentId}
                assignees={assignees}
                onChange={function(assigneeId) {
                  if (onAssign) onAssign(row.id, assigneeId);
                }}
            />
        );
      },
      exportValue: (row, column) => row.assignedTo ? row.assignedTo.name : ""
    });
  } else {
    columns.push({
      field: "assignedTo.name",
      displayField: "assignedTo",
      label: "Vyřizuje",
      type: ColumnType.TEXT,
      mapper: function(field) { return field ? field.name : ""; },
      exportValue: (row, column) => row.assignedTo ? row.assignedTo.name : "",
      width: "10%",
    });
  }

  columns.push({
    field: "assignedDate",
    label: "Datum přidělení",
    type: ColumnType.DATE,
    width: "8%",
  });

  columns.push({
    field: "processingDate",
    label: "Vyřešeno",
    type: ColumnType.DATE,
    width: "8%",
    mapper: (field, row) => row.state !== InitiativeStateRaw.NEW ? row.processingDate : "",
  });

  return <CommonGrid {...rest} columns={columns} />;
};

export default InitiativeGrid;